// Ingest slice — Isomorphism Phase 2 · Blind Spot 1 (AppState slicing)
//
// Ingest pipeline state: progress tracking, generation status, lint report.
// Written by CourseView during generation. Read by IngestProgress component.
//
// This slice replaces the useState<IngestProgressState> in CourseView.

import type { StateCreator } from 'zustand';
import type { LintReport } from '@examforge/shared/schemas';
import { initialProgress, reduceIngestEvent, type IngestProgressState } from '../../components/ingest-progress.js';

export type IngestStatus = 'idle' | 'generating' | 'done' | 'error';

export interface IngestSlice {
  // State
  /** Streaming ingest progress (stage-by-stage updates). */
  ingestProgress: IngestProgressState;
  /** High-level generation status. */
  ingestStatus: IngestStatus;
  /** Error message (user-friendly, already mapped through toFriendlyMessage). */
  ingestError: string | null;
  /** Lint report from last pipeline-complete event. */
  lintReport: LintReport | undefined;

  // Actions
  setIngestStatus: (status: IngestStatus) => void;
  setIngestError: (error: string | null) => void;
  /** Apply a single ingest event to the progress state. */
  reduceEvent: (event: Parameters<typeof reduceIngestEvent>[1]) => void;
  setLintReport: (report: LintReport | undefined) => void;
  resetIngest: () => void;
}

const initialIngestState = {
  ingestProgress: initialProgress,
  ingestStatus: 'idle' as IngestStatus,
  ingestError: null as string | null,
  lintReport: undefined as LintReport | undefined,
};

export const createIngestSlice: StateCreator<IngestSlice> = (set) => ({
  ...initialIngestState,

  setIngestStatus: (ingestStatus) => set({ ingestStatus }),
  setIngestError: (ingestError) => set({ ingestError }),
  reduceEvent: (event) =>
    set((s) => ({ ingestProgress: reduceIngestEvent(s.ingestProgress, event) })),
  setLintReport: (lintReport) => set({ lintReport }),
  resetIngest: () => set(initialIngestState),
});
